import { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../../../../../app/store';
import {
  updateProcedureAction as updateProcedure,
  removeProcedureAction as removeProcedure
} from '../../../../../../services/EncounterService/slice';
import { useGetServicesQuery, useGetRoomsQuery } from '../../../../../../services/ConfigurationService';
import type { ProcedureType } from '../../../../../../services/EncounterService/types/encounterTypes';
import type { ServiceType, RoomType } from '../../../../../../services/ConfigurationService/types';

// Helper function to calculate end time based on start time and duration
const calculateEndTime = (startTime: string, duration: string): string => {
  if (!startTime || !duration || isNaN(Number(duration))) {
    return '';
  }

  const startDate = new Date(startTime);
  const endDate = new Date(startDate.getTime() + Number(duration) * 60 * 1000);

  // Format as local datetime string (YYYY-MM-DDTHH:MM) without UTC conversion
  const year = endDate.getFullYear();
  const month = String(endDate.getMonth() + 1).padStart(2, '0');
  const day = String(endDate.getDate()).padStart(2, '0');
  const hours = String(endDate.getHours()).padStart(2, '0');
  const minutes = String(endDate.getMinutes()).padStart(2, '0');

  return `${year}-${month}-${day}T${hours}:${minutes}`;
};

export interface ServiceFormData {
  priority: number;
  isPrimary: boolean;
  modifier: string;
  description: string;
  procedureStart: string;
  procedureEnd: string;
  procedureDuration: string;
  procedureTimeUnits: string;
  serviceCode: string;
  serviceDescription: string;
  room: string;
}

// Helper function to create initial form data from procedure
const createFormDataFromProcedure = (procedure: ProcedureType): ServiceFormData => {
  const formData: ServiceFormData = {
    priority: procedure.priority || 1,
    isPrimary: procedure.isPrimary || false,
    modifier: procedure.modifier || '',
    description: procedure.description || '',
    procedureStart: procedure.procedureStart || '',
    procedureEnd: procedure.procedureEnd || '',
    procedureDuration: procedure.procedureDuration?.toString() || '',
    procedureTimeUnits: 'min',
    serviceCode: procedure.serviceCode || '',
    serviceDescription: procedure.serviceDescription || '',
    room: procedure.room || ''
  };

  // Calculate end time if we have start time and duration
  if (formData.procedureStart && formData.procedureDuration && !isNaN(Number(formData.procedureDuration))) {
    formData.procedureEnd = calculateEndTime(formData.procedureStart, formData.procedureDuration);
  }

  return formData;
};

interface UseServiceFormReturn {
  formData: ServiceFormData;
  selectedService: ServiceType | null;
  selectedRoom: RoomType | null;
  error: string;
  services: ServiceType[];
  rooms: RoomType[];
  isEncounterCancelled: boolean;
  handleInputChange: (field: keyof ServiceFormData, value: string | number | boolean) => void;
  handleServiceChange: (service: ServiceType | null) => void;
  handleRoomChange: (room: RoomType | null) => void;
  handleSubmit: () => void;
  handleDelete: () => void;
  handleClose: () => void;
  setError: (error: string) => void;
}

export const useServiceForm = (
  procedure: ProcedureType | null,
  open: boolean,
  onClose: () => void,
  onServiceUpdated?: () => void
): UseServiceFormReturn => {
  const [searchParams] = useSearchParams();
  const dispatch = useAppDispatch();

  const company = searchParams.get('company');
  const center = searchParams.get('center');
  const centerId = company && center ? `${company}-${center}` : '';

  // Get current encounter for status check
  const encounter = useAppSelector(state => state.encounter.currentEncounter);
  const isEncounterCancelled = encounter?.status === 'CANCEL';

  // Form state
  const [formData, setFormData] = useState<ServiceFormData>({
    priority: 1,
    isPrimary: false,
    modifier: '',
    description: '',
    procedureStart: '',
    procedureEnd: '',
    procedureDuration: '',
    procedureTimeUnits: 'min',
    serviceCode: '',
    serviceDescription: '',
    room: ''
  });

  const [selectedService, setSelectedService] = useState<ServiceType | null>(null);
  const [selectedRoom, setSelectedRoom] = useState<RoomType | null>(null);
  const [error, setError] = useState('');

  // Fetch configuration data
  const { data: services = [] } = useGetServicesQuery({ centerId }, { skip: !centerId });
  const { data: rooms = [] } = useGetRoomsQuery({ centerId }, { skip: !centerId });

  // Populate form when procedure changes
  useEffect(() => {
    if (procedure && open) {
      const newFormData = createFormDataFromProcedure(procedure);
      setFormData(newFormData);

      // Find and set matching service
      const matchingService = services.find(s => s.code === procedure.serviceCode);
      setSelectedService(matchingService || null);

      // Find and set matching room
      const matchingRoom = rooms.find(r => r.code === procedure.room);
      setSelectedRoom(matchingRoom || null);

      setError('');
    }
  }, [procedure, open, services, rooms]);

  // Update form data when procedure priority changes in Redux state
  useEffect(() => {
    if (procedure && open) {
      setFormData(prev => ({
        ...prev,
        priority: procedure.priority || 1,
        isPrimary: (procedure.priority || 1) === 1
      }));
    }
  }, [procedure?.priority, open]);

  const handleClose = useCallback(() => {
    onClose();
    setError('');
  }, [onClose]);

  const handleInputChange = useCallback((field: keyof ServiceFormData, value: string | number | boolean) => {
    setFormData(prev => {
      const newData = { ...prev, [field]: value };

      // Auto-update isPrimary when priority changes
      if (field === 'priority') {
        newData.isPrimary = Number(value) === 1;
      }

      // Calculate end time when start time or duration changes
      if (field === 'procedureStart' || field === 'procedureDuration') {
        const startTime = field === 'procedureStart' ? String(value) : newData.procedureStart;
        const duration = field === 'procedureDuration' ? String(value) : newData.procedureDuration;
        newData.procedureEnd = calculateEndTime(startTime, duration);
      }

      return newData;
    });
  }, []);

  const handleServiceChange = useCallback((service: ServiceType | null) => {
    setSelectedService(service);
    if (service) {
      setFormData(prev => ({
        ...prev,
        serviceCode: service.code,
        serviceDescription: service.description
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        serviceCode: '',
        serviceDescription: ''
      }));
    }
  }, []);

  const handleRoomChange = useCallback((room: RoomType | null) => {
    setSelectedRoom(room);
    if (room) {
      setFormData(prev => ({ ...prev, room: room.code }));
    } else {
      setFormData(prev => ({ ...prev, room: '' }));
    }
  }, []);

  const handleSubmit = useCallback(() => {
    if (!procedure) return;

    // Create updated procedure
    const updatedProcedure: ProcedureType = {
      ...procedure,
      priority: formData.priority,
      isPrimary: formData.isPrimary,
      modifier: formData.modifier || undefined,
      description: formData.description || undefined,
      procedureStart: formData.procedureStart || undefined,
      procedureEnd: formData.procedureEnd || undefined,
      procedureDuration: formData.procedureDuration ? Number(formData.procedureDuration) : undefined,
      procedureTimeUnits: formData.procedureTimeUnits || undefined,
      serviceCode: formData.serviceCode || undefined,
      serviceDescription: formData.serviceDescription || undefined,
      room: formData.room || undefined
    };

    // Update procedure in encounter state
    dispatch(updateProcedure(updatedProcedure));

    // Call callback if provided
    onServiceUpdated?.();

    handleClose();
  }, [procedure, formData, dispatch, onServiceUpdated, handleClose]);

  const handleDelete = useCallback(() => {
    if (!procedure || !procedure.id) return;

    // Remove procedure from encounter state
    dispatch(removeProcedure(procedure.id));

    // Call callback if provided
    onServiceUpdated?.();

    handleClose();
  }, [procedure, dispatch, onServiceUpdated, handleClose]);

  return {
    formData,
    selectedService,
    selectedRoom,
    error,
    services,
    rooms,
    isEncounterCancelled,
    handleInputChange,
    handleServiceChange,
    handleRoomChange,
    handleSubmit,
    handleDelete,
    handleClose,
    setError
  };
};
